import React, { useState, useEffect, useRef } from 'react';
import { knowledgeApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Search, BookOpen, Eye, Tag, Sparkles, Upload, FileText, File, X } from 'lucide-react';
import { toast } from 'sonner';

const DRAGON_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/ot58j39v_Transparent%20BG%20Dragon.png';

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'culture', label: 'Culture & Values' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'procedures', label: 'Procedures' },
  { value: 'templates', label: 'Templates' },
  { value: 'policy', label: 'Policies' },
  { value: 'guideline', label: 'Guidelines' },
  { value: 'isms', label: 'ISMS' },
  { value: 'how_to', label: 'How-To' },
  { value: 'sop', label: 'SOP' },
  { value: 'general', label: 'General' },
];

const getCategoryColor = (category) => {
  const colors = {
    culture: 'bg-[#E171EA]/10 text-[#E171EA]',
    compliance: 'bg-[#106EFD]/10 text-[#106EFD]',
    procedures: 'bg-[#37C563]/10 text-[#37C563]',
    templates: 'bg-[#FFD166]/10 text-[#FFD166]',
    policy: 'bg-[#1A2B5E]/10 text-[#1A2B5E]',
    guideline: 'bg-[#40CFDE]/10 text-[#40CFDE]',
    isms: 'bg-red-100 text-red-700',
    how_to: 'bg-orange-100 text-orange-700',
    sop: 'bg-purple-100 text-purple-700',
  };
  return colors[category] || 'bg-navy/10 text-navy';
};

const Knowledge = () => {
  const fileInputRef = useRef(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('policy');
  const [viewArticle, setViewArticle] = useState(null);
  const [newArticle, setNewArticle] = useState({
    title: '', content: '', category: 'general', tags: ''
  });

  const fetchArticles = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      const response = await knowledgeApi.getAll(params);
      setArticles(response.data);
    } catch { toast.error('Failed to fetch articles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchArticles(); }, [search, selectedCategory]);

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    try {
      const articleData = {
        ...newArticle,
        tags: newArticle.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      await knowledgeApi.create(articleData);
      toast.success('Article created!');
      setDialogOpen(false);
      setNewArticle({ title: '', content: '', category: 'general', tags: '' });
      fetchArticles();
    } catch { toast.error('Failed to create article'); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'txt'].includes(ext)) {
      toast.error('Only PDF, DOCX, and TXT files are supported');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setUploading(true);
    try {
      await knowledgeApi.upload(file, uploadCategory);
      toast.success(`"${file.name}" uploaded and added to Knowledge Base!`);
      setUploadDialogOpen(false);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Knowledge Base</h1>
          <p className="text-navy/60 mt-1">CompliSun policies, procedures, and resources</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-file-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload File
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle className="font-['Amaranth']">Upload Document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Document Category</Label>
                  <Select value={uploadCategory} onValueChange={setUploadCategory}>
                    <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="policy">Policy</SelectItem>
                      <SelectItem value="procedure">Procedure</SelectItem>
                      <SelectItem value="guideline">Guideline</SelectItem>
                      <SelectItem value="isms">ISMS</SelectItem>
                      <SelectItem value="how_to">How-To</SelectItem>
                      <SelectItem value="sop">SOP</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div
                  className="border-2 border-dashed border-navy/20 rounded-xl p-8 text-center hover:border-[#40CFDE] transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-[#40CFDE]', 'bg-[#40CFDE]/5'); }}
                  onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-[#40CFDE]', 'bg-[#40CFDE]/5'); }}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileUpload(f); }}
                  data-testid="upload-dropzone"
                >
                  <FileText className="w-12 h-12 mx-auto mb-3 text-[#40CFDE]" />
                  <p className="font-medium text-[#1A2B5E]">Drop your file here or click to browse</p>
                  <p className="text-sm text-navy/50 mt-1">PDF, DOCX, or TXT (max 10MB)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
                  />
                </div>
                {uploading && (
                  <div className="flex items-center gap-2 text-[#40CFDE]">
                    <div className="w-5 h-5 loading-spinner" />
                    <span>Uploading and generating AI summary...</span>
                  </div>
                )}
                <div className="bg-[#F5F8FF] rounded-lg p-3 text-sm text-navy/60">
                  <p className="font-medium text-[#1A2B5E] mb-1">How it works:</p>
                  <p>Upload your document. AI will generate bullet-point key takeaways. The original file is stored so you can open it anytime in a new tab.</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-article-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Article
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="font-['Amaranth']">Create Knowledge Article</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateArticle} className="space-y-4 mt-4">
                <div className="space-y-2"><Label>Title *</Label>
                  <Input value={newArticle.title} onChange={e => setNewArticle({...newArticle, title: e.target.value})} placeholder="Article title" required data-testid="article-title-input" /></div>
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={newArticle.category} onValueChange={v => setNewArticle({...newArticle, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Content *</Label>
                  <Textarea value={newArticle.content} onChange={e => setNewArticle({...newArticle, content: e.target.value})} placeholder="Write your article content..." rows={8} required data-testid="article-content-input" /></div>
                <div className="space-y-2"><Label>Tags (comma-separated)</Label>
                  <Input value={newArticle.tags} onChange={e => setNewArticle({...newArticle, tags: e.target.value})} placeholder="e.g., bsa, aml, training" data-testid="article-tags-input" /></div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-article-btn">Create Article</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="knowledge-search-input" />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {categories.map(cat => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* AI hint */}
      <Card className="bg-gradient-to-r from-[#83E3DE]/10 to-[#E171EA]/10 border-none">
        <CardContent className="p-4 flex items-center gap-4">
          <img src={DRAGON_MASCOT} alt="Dragon" className="w-12 h-12" />
          <div>
            <p className="font-medium text-[#1A2B5E] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E171EA]" /> AI Knowledge Agent</p>
            <p className="text-sm text-navy/60">Ask questions and I'll find relevant articles and guidance for you.</p>
          </div>
          <Button variant="outline" className="ml-auto" data-testid="ask-ai-btn">Ask AI</Button>
        </CardContent>
      </Card>

      {/* Articles grid */}
      {articles.length === 0 ? (
        <div className="empty-state py-16">
          <img src={DRAGON_MASCOT} alt="Dragon" className="w-32 h-32 mb-4" />
          <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No articles found</h3>
          <p className="text-navy/60 mb-4">{search || selectedCategory !== 'all' ? 'Try adjusting your filters' : 'Start building your knowledge base'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map(article => (
            <Card key={article.id} className="bg-white border-navy/10 card-hover cursor-pointer" onClick={() => setViewArticle(article)} data-testid={`article-card-${article.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-2">
                  <Badge className={getCategoryColor(article.category)}>{article.category}</Badge>
                  <div className="flex items-center gap-2">
                    {article.source_file && <File className="w-3 h-3 text-navy/40" title={`Source: ${article.source_file}`} />}
                    <span className="text-xs text-navy/40 flex items-center gap-1"><Eye className="w-3 h-3" />{article.views || 0}</span>
                  </div>
                </div>
                <h3 className="font-semibold text-[#1A2B5E] mt-3 line-clamp-2">{article.title}</h3>
                <p className="text-sm text-navy/60 mt-2 line-clamp-3">{article.content}</p>
                {article.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-4">
                    {article.tags.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs"><Tag className="w-2 h-2 mr-1" />{tag}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-navy/40 mt-4">{new Date(article.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View article dialog */}
      <Dialog open={!!viewArticle} onOpenChange={() => setViewArticle(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          {viewArticle && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getCategoryColor(viewArticle.category)}>{viewArticle.category}</Badge>
                  <span className="text-xs text-navy/40 flex items-center gap-1"><Eye className="w-3 h-3" />{viewArticle.views || 0} views</span>
                  {viewArticle.source_file && <Badge variant="outline" className="text-xs"><File className="w-3 h-3 mr-1" />{viewArticle.source_file}</Badge>}
                </div>
                <DialogTitle className="font-['Amaranth'] text-2xl">{viewArticle.title}</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                {/* Open file button */}
                {viewArticle.stored_filename && (
                  <div className="mb-4 p-3 bg-[#40CFDE]/10 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#1A2B5E]" />
                      <div>
                        <p className="text-sm font-medium text-[#1A2B5E]">{viewArticle.source_file}</p>
                        {viewArticle.file_size && <p className="text-xs text-navy/50">{(viewArticle.file_size / 1024).toFixed(0)} KB</p>}
                      </div>
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/file/${viewArticle.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-[#1A2B5E] text-white rounded-lg text-sm font-medium hover:bg-[#1A2B5E]/90 transition-colors"
                      data-testid="open-file-btn"
                    >
                      Open File
                    </a>
                  </div>
                )}
                
                {/* AI Summary / Content */}
                <div className="mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#40CFDE]" />
                  <h3 className="text-sm font-semibold text-[#1A2B5E]">
                    {viewArticle.stored_filename ? 'Key Takeaways' : 'Content'}
                  </h3>
                </div>
                <div className="prose prose-sm max-w-none text-navy/80 whitespace-pre-wrap" data-testid="article-content">{viewArticle.content}</div>
                
                {viewArticle.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-navy/10">
                    {viewArticle.tags.map((tag, idx) => <Badge key={idx} variant="outline"><Tag className="w-3 h-3 mr-1" />{tag}</Badge>)}
                  </div>
                )}
                <p className="text-sm text-navy/40 mt-4">Published: {new Date(viewArticle.created_at).toLocaleDateString()}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Knowledge;
